/**
 * The part of the public trip page meant for a rider someone else booked
 * for (a child, a parent, a friend across town). Shows what to pay, the
 * pickup code to tell the driver, lets the rider send their exact spot
 * to the driver, and lets them say "I've arrived" so the booker knows.
 *
 * Every write goes through anon RPCs keyed by the share token
 * (`share_rider_location`, `confirm_rider_arrived`); the server decides
 * whether the ride is in a state where the write is allowed.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Banknote, CheckCircle2, KeyRound, LocateFixed, MapPinCheck, ShieldCheck } from 'lucide-react';

export interface SharedTripRider {
  first_name: string;
  elsewhere: boolean;
  fare_xcd: number | string | null;
  seats: number;
  pickup_code: string | null;
  code_checked: boolean;
  location_shared_at: string | null;
  arrived_at: string | null;
  dropped_off_at_stop: string | null;
  dropoff_off_by_m: number | null;
  /** Address or landmark the booker pinned for the pickup. */
  pickup_label?: string | null;
  pickup_pinned?: boolean;
}

interface TripRiderPanelProps {
  token: string;
  rider: SharedTripRider;
  status: string | undefined;
  originName: string | undefined;
  pickedUpAt: string | null | undefined;
  now: number;
  onChanged: () => void;
}

/** How often a moving rider's position is sent to the driver. */
const SEND_EVERY_MS = 10_000;
/** Resend the last fix this often so a rider standing still doesn't look stale. */
const KEEPALIVE_MS = 20_000;
/** Drop-off within this distance of the booked stop counts as "at the stop". */
const AT_STOP_M = 400;

type ShareState = 'idle' | 'asking' | 'sharing' | 'denied' | 'unavailable' | 'outside';

function formatFare(fare: number | string | null): string {
  const n = Number(fare);
  return `EC$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
}

function secondsAgo(iso: string | null, now: number): string | null {
  if (!iso) return null;
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (!Number.isFinite(s)) return null;
  if (s < 15) return 'just now';
  if (s < 60) return `${s}s ago`;
  return `${Math.round(s / 60)} min ago`;
}

/** Rider-facing card on the shared trip page. */
export default function TripRiderPanel({
  token,
  rider,
  status,
  originName,
  pickedUpAt,
  now,
  onChanged,
}: TripRiderPanelProps) {
  const name = rider.first_name || 'there';
  const waiting = status === 'accepted' && !pickedUpAt;
  const onTrip = status === 'in_progress' || status === 'completed';
  const canShareLocation = rider.elsewhere && waiting;

  const [share, setShare] = useState<ShareState>('idle');
  const [lastSentAt, setLastSentAt] = useState<number | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const lastFixRef = useRef<GeolocationPosition | null>(null);
  const lastSendRef = useRef(0);
  const [arriving, setArriving] = useState(false);
  const [arrivedAt, setArrivedAt] = useState<string | null>(rider.arrived_at);
  const [arriveError, setArriveError] = useState<string | null>(null);

  useEffect(() => {
    if (rider.arrived_at) setArrivedAt(rider.arrived_at);
  }, [rider.arrived_at]);

  const stopSharing = useCallback(() => {
    if (watchIdRef.current != null) navigator.geolocation?.clearWatch(watchIdRef.current);
    watchIdRef.current = null;
    lastFixRef.current = null;
    setShare((s) => (s === 'sharing' || s === 'asking' ? 'idle' : s));
  }, []);

  const send = useCallback(
    async (pos: GeolocationPosition) => {
      lastSendRef.current = Date.now();
      const { data, error } = await supabase.rpc('share_rider_location', {
        p_token: token,
        p_lat: pos.coords.latitude,
        p_lng: pos.coords.longitude,
        p_accuracy_m: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
      });
      const result = data as { ok?: boolean; error?: string } | null;
      if (error || !result?.ok) {
        if (result?.error === 'outside_dominica') {
          stopSharing();
          setShare('outside');
        } else if (result?.error === 'not_waiting') {
          stopSharing();
          onChanged();
        }
        return;
      }
      setLastSentAt(Date.now());
      setShare('sharing');
    },
    [token, stopSharing, onChanged],
  );

  const startSharing = useCallback(() => {
    if (!navigator.geolocation) {
      setShare('unavailable');
      return;
    }
    setShare('asking');
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        lastFixRef.current = pos;
        if (Date.now() - lastSendRef.current >= SEND_EVERY_MS) void send(pos);
      },
      (err) => {
        stopSharing();
        setShare(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
      },
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 30_000 },
    );
  }, [send, stopSharing]);

  useEffect(() => {
    if (share !== 'sharing') return;
    const id = setInterval(() => {
      const fix = lastFixRef.current;
      if (fix && Date.now() - lastSendRef.current >= KEEPALIVE_MS) void send(fix);
    }, 5_000);
    return () => clearInterval(id);
  }, [share, send]);

  // The bus has picked them up (or the ride ended): their spot no longer matters.
  useEffect(() => {
    if (!canShareLocation) stopSharing();
  }, [canShareLocation, stopSharing]);

  useEffect(() => () => {
    if (watchIdRef.current != null) navigator.geolocation?.clearWatch(watchIdRef.current);
  }, []);

  const confirmArrived = async () => {
    setArriving(true);
    setArriveError(null);
    const { data, error } = await supabase.rpc('confirm_rider_arrived', { p_token: token });
    setArriving(false);
    const result = data as { ok?: boolean; arrived_at?: string } | null;
    if (error || !result?.ok) {
      setArriveError('We could not send that. Check your connection and try again.');
      return;
    }
    setArrivedAt(result.arrived_at ?? new Date().toISOString());
    onChanged();
  };

  const fare = formatFare(rider.fare_xcd);
  const sharedAgo = secondsAgo(
    lastSentAt ? new Date(lastSentAt).toISOString() : rider.location_shared_at,
    now,
  );
  const atStop = rider.dropoff_off_by_m != null && rider.dropoff_off_by_m <= AT_STOP_M;

  return (
    <section className="px-4 py-4 border-b border-white/10 space-y-3" data-testid="trip-rider-panel">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-400">
          This ride is for you
        </p>
        <h2 className="font-sans text-lg font-extrabold leading-snug">
          {waiting
            ? `Hi ${name}, your bus is coming to ${
                (rider.pickup_pinned && rider.pickup_label) || originName || 'your stop'
              }`
            : status === 'in_progress'
              ? `Hi ${name}, enjoy the ride`
              : status === 'completed'
                ? `You're there, ${name}`
                : `Hi ${name}`}
        </h2>
      </div>

      {status !== 'completed' ? (
        <div className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-3">
          <Banknote className="w-6 h-6 text-amber-300 shrink-0" />
          <div className="min-w-0">
            <p className="text-base font-bold">
              Pay <span className="text-amber-300">{fare}</span> cash to the driver
            </p>
            <p className="text-xs text-white/50">
              {rider.seats > 1 ? `Covers ${rider.seats} seats. ` : ''}Bus fares are set by the government.
            </p>
          </div>
        </div>
      ) : null}

      {rider.pickup_code ? (
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-3 py-3">
          <div className="flex items-center gap-2 text-emerald-300">
            <KeyRound className="w-4 h-4" />
            <p className="text-xs font-bold uppercase tracking-wider">Your pickup code</p>
          </div>
          <div className="mt-2 flex gap-2" data-testid="trip-rider-code">
            {rider.pickup_code.split('').map((digit, i) => (
              <span
                key={i}
                className="w-12 h-14 rounded-lg bg-gray-900 border border-white/15 flex items-center justify-center text-3xl font-extrabold font-mono"
              >
                {digit}
              </span>
            ))}
          </div>
          <p className="mt-2 text-sm text-white/70">
            Tell the driver this code when you get on. Don't share it with anyone else.
          </p>
        </div>
      ) : rider.code_checked ? (
        <div className="flex items-center gap-2 text-sm text-emerald-300">
          <ShieldCheck className="w-4 h-4" />
          Pickup code checked by the driver.
        </div>
      ) : null}

      {canShareLocation ? (
        <div className="rounded-xl bg-white/5 px-3 py-3">
          {share === 'sharing' ? (
            <div className="flex items-start gap-3">
              <span className="mt-1.5 w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold">The driver can see where you are</p>
                <p className="text-xs text-white/50">
                  {sharedAgo ? `Updated ${sharedAgo}. ` : ''}Keep this page open until you're on the bus.
                </p>
              </div>
              <button
                type="button"
                onClick={stopSharing}
                className="text-xs font-semibold text-white/60 underline underline-offset-2 shrink-0"
              >
                Stop
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={startSharing}
                disabled={share === 'asking'}
                className="w-full h-14 rounded-xl bg-emerald-500 text-gray-950 font-extrabold text-base flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="trip-rider-share-location"
              >
                <LocateFixed className="w-5 h-5" />
                {share === 'asking' ? 'Finding you…' : 'Show the driver where I am'}
              </button>
              <p className="mt-2 text-xs text-white/50">
                {share === 'denied'
                  ? 'Location is blocked for this page. Allow it in your browser settings, or just wait at the stop.'
                  : share === 'outside'
                    ? 'Your location looks like it is outside Dominica, so it was not sent.'
                    : share === 'unavailable'
                      ? 'We could not get your location. Wait at the stop and the driver will find you.'
                      : `Only while you wait. It stops as soon as you're on the bus.`}
              </p>
            </>
          )}
        </div>
      ) : null}

      {status === 'completed' && rider.dropped_off_at_stop ? (
        <div className="flex items-start gap-2 text-sm text-white/80">
          <MapPinCheck className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
          <span>
            Dropped off at {rider.dropped_off_at_stop}.
            {rider.dropoff_off_by_m != null
              ? atStop
                ? ' The bus GPS confirms the stop.'
                : ` The bus was ${(rider.dropoff_off_by_m / 1000).toFixed(1)} km from the stop.`
              : ''}
          </span>
        </div>
      ) : null}

      {onTrip && pickedUpAt ? (
        arrivedAt ? (
          <div className="flex items-center gap-2 rounded-xl bg-emerald-500/10 px-3 py-3 text-sm font-semibold text-emerald-300">
            <CheckCircle2 className="w-5 h-5" />
            We told the person who booked your ride that you arrived.
          </div>
        ) : (
          <div>
            <button
              type="button"
              onClick={() => void confirmArrived()}
              disabled={arriving}
              className={`w-full h-14 rounded-xl font-extrabold text-base flex items-center justify-center gap-2 disabled:opacity-60 ${
                status === 'completed'
                  ? 'bg-emerald-500 text-gray-950'
                  : 'bg-white/10 text-white border border-white/15'
              }`}
              data-testid="trip-rider-arrived"
            >
              <CheckCircle2 className="w-5 h-5" />
              {arriving ? 'Sending…' : "I've arrived"}
            </button>
            <p className="mt-2 text-xs text-white/50">
              {arriveError ?? 'Tap when you get there so the person who booked knows you are safe.'}
            </p>
          </div>
        )
      ) : null}
    </section>
  );
}
